// apps/admin-ui/src/pages/ContractsPage.tsx
// Contract Rule Browser — "Source of truth for humans"
// Shows English prose rules, constants, decision tables, and source references
// ALL RIGHTS RESERVED
//
// UPDATED 2026-02-04: Added URL param support (?contract=FeeContract)

import { useState, useMemo, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CONTRACT_RULES,
  DOMAIN_LABELS,
  DOMAIN_ORDER,
  getContractsByDomain,
} from '../config/contractRules';
import type { ContractDefinition, DecisionRow } from '../config/contractRules';
import { SourceViewerDialog } from '../components/SourceViewerDialog';
import { api } from '../api/client';
import '../styles/contracts.css';
import '../styles/contract-reviews.css';

// =============================================================================
// TYPES
// =============================================================================

type ViewMode = 'rules' | 'decisions';

// =============================================================================
// MAIN PAGE
// =============================================================================

export function ContractsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Initialize from URL param or default to first contract
  const initialContract = searchParams.get('contract') || CONTRACT_RULES[0].id;
  const [selectedId, setSelectedId] = useState<string>(
    CONTRACT_RULES.find(c => c.id === initialContract) ? initialContract : CONTRACT_RULES[0].id
  );
  const [viewMode, setViewMode] = useState<ViewMode>('rules');
  const [searchQuery, setSearchQuery] = useState('');
  const [sourceViewerFile, setSourceViewerFile] = useState<string | null>(null);

  // Update URL when selection changes
  useEffect(() => {
    const currentParam = searchParams.get('contract');
    if (currentParam !== selectedId) {
      setSearchParams({ contract: selectedId }, { replace: true });
    }
  }, [selectedId, searchParams, setSearchParams]);

  // Handle URL changes (e.g., back/forward navigation)
  useEffect(() => {
    const contractParam = searchParams.get('contract');
    if (contractParam && contractParam !== selectedId) {
      const found = CONTRACT_RULES.find(c => c.id === contractParam);
      if (found) {
        setSelectedId(contractParam);
      }
    }
  }, [searchParams]);

  const byDomain = useMemo(() => getContractsByDomain(), []);

  const selected = useMemo(
    () => CONTRACT_RULES.find(c => c.id === selectedId) || CONTRACT_RULES[0],
    [selectedId],
  );

  const filteredByDomain = useMemo(() => {
    if (!searchQuery.trim()) return byDomain;
    const q = searchQuery.toLowerCase();
    const filtered: Record<string, ContractDefinition[]> = {};
    for (const [domain, contracts] of Object.entries(byDomain)) {
      const matching = contracts.filter(c =>
        c.name.toLowerCase().includes(q) ||
        c.description.toLowerCase().includes(q) ||
        c.domain.toLowerCase().includes(q) ||
        c.constants.some(k => k.name.toLowerCase().includes(q)) ||
        c.rules.some(r => r.summary.toLowerCase().includes(q))
      );
      if (matching.length) filtered[domain] = matching;
    }
    return filtered;
  }, [byDomain, searchQuery]);

  return (
    <div className="contracts-page">
      {/* ─── Sidebar: Contract List ─── */}
      <aside className="contracts-sidebar">
        <div className="contracts-sidebar-header">
          <h2 className="contracts-sidebar-title">Business Rules</h2>
          <span className="contracts-count">{CONTRACT_RULES.length} contracts</span>
        </div>

        <div className="contracts-search-box">
          <input
            type="text"
            placeholder="Filter contracts…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="contracts-search-input"
          />
        </div>

        <nav className="contracts-nav">
          {DOMAIN_ORDER.map(domain => {
            const contracts = filteredByDomain[domain];
            if (!contracts?.length) return null;
            return (
              <div key={domain} className="contracts-domain-group">
                <div className="contracts-domain-label">
                  <span
                    className="contracts-domain-dot"
                    style={{ backgroundColor: contracts[0].domainColor }}
                  />
                  {DOMAIN_LABELS[domain] || domain}
                </div>
                {contracts.map(c => (
                  <button
                    key={c.id}
                    className={`contracts-nav-item ${selectedId === c.id ? 'active' : ''}`}
                    onClick={() => setSelectedId(c.id)}
                  >
                    {c.name}
                  </button>
                ))}
              </div>
            );
          })}
        </nav>
      </aside>

      {/* ─── Main Content ─── */}
      <main className="contracts-main">
        <ContractDetail
          contract={selected}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          onViewSource={setSourceViewerFile}
        />
      </main>

      {/* ─── Source Viewer Dialog (OTP-gated) ─── */}
      {sourceViewerFile && (
        <SourceViewerDialog
          sourceFile={sourceViewerFile}
          onClose={() => setSourceViewerFile(null)}
        />
      )}
    </div>
  );
}

// =============================================================================
// CONTRACT DETAIL
// =============================================================================

// Contract review status type
type ContractStatus = 'pending' | 'under_review' | 'needs_attention' | 'approved' | 'rejected';

const STATUS_OPTIONS: { value: ContractStatus; label: string; color: string }[] = [
  { value: 'pending', label: 'Pending', color: '#6b7280' },
  { value: 'under_review', label: 'Under Review', color: '#3b82f6' },
  { value: 'needs_attention', label: 'Needs Attention', color: '#f59e0b' },
  { value: 'approved', label: 'Approved', color: '#10b981' },
  { value: 'rejected', label: 'Rejected', color: '#ef4444' },
];

function ContractDetail({
  contract,
  viewMode,
  onViewModeChange,
  onViewSource,
}: {
  contract: ContractDefinition;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  onViewSource: (file: string) => void;
}) {
  const [showSource, setShowSource] = useState(false);
  const [reviewStatus, setReviewStatus] = useState<ContractStatus | null>(null);
  const [reviewId, setReviewId] = useState<number | null>(null);
  const [versionTag, setVersionTag] = useState<string | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!statusDropdownOpen) return;
    
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.status-dropdown-wrapper') && !target.closest('.status-dropdown-menu-fixed')) {
        setStatusDropdownOpen(false);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [statusDropdownOpen]);

  // Fetch review status for this contract
  useEffect(() => {
    async function fetchReviewStatus() {
      try {
        // Get the most recent review that includes this contract
        const data = await api.get('/api/v1/admin/contracts/reviews?pageSize=50');
        const reviews = data.data || [];
        console.log('[ContractsPage] Fetched reviews:', reviews.length, 'looking for:', contract.id);
        
        // Find most recent review containing this contract
        for (const review of reviews) {
          const contracts = review.contractsChanged || [];
          console.log('[ContractsPage] Review', review.versionTag, 'has contracts:', contracts);
          
          const found = contracts.find((c: any) => {
            const contractName = typeof c === 'string' ? c : c?.name;
            return contractName === contract.id || contractName === contract.name;
          });
          
          if (found) {
            console.log('[ContractsPage] Found contract in review:', review.versionTag, found);
            setReviewId(review.id);
            setVersionTag(review.versionTag);
            setReviewStatus(typeof found === 'string' ? 'pending' : (found.status || 'pending'));
            return;
          }
        }
        // Not found in any review
        console.log('[ContractsPage] Contract not found in any review');
        setReviewStatus(null);
        setReviewId(null);
        setVersionTag(null);
      } catch (err) {
        console.error('[ContractsPage] Failed to fetch review status:', err);
      }
    }
    
    fetchReviewStatus();
  }, [contract.id, contract.name]);

  // Update contract status
  async function handleStatusChange(newStatus: ContractStatus) {
    if (!reviewId) return;
    
    setStatusLoading(true);
    setStatusDropdownOpen(false);
    
    try {
      // Use fetch with auth header for PATCH (api client doesn't have patch method)
      const res = await fetch(
        `/api/v1/admin/contracts/reviews/${reviewId}/contracts/${encodeURIComponent(contract.id)}`,
        {
          method: 'PATCH',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': 'Bearer admin_test',  // Dev mode auth
          },
          body: JSON.stringify({ status: newStatus }),
        }
      );
      
      if (res.ok) {
        setReviewStatus(newStatus);
      } else {
        const err = await res.json();
        alert(`Failed to update status: ${err.message || 'Unknown error'}`);
      }
    } catch (err: any) {
      console.error('Failed to update status:', err);
      alert(`Failed to update status: ${err.message || 'Unknown error'}`);
    } finally {
      setStatusLoading(false);
    }
  }

  const currentStatusOption = STATUS_OPTIONS.find(s => s.value === reviewStatus);

  return (
    <div className="contract-detail">
      {/* Header */}
      <header className="contract-header">
        <div className="contract-header-top">
          <div>
            <h1 className="contract-title">{contract.name}</h1>
            <p className="contract-description">{contract.description}</p>
          </div>
          <div className="contract-badges">
            <span
              className="contract-domain-badge"
              style={{ borderColor: contract.domainColor, color: contract.domainColor }}
            >
              {DOMAIN_LABELS[contract.domain]}
            </span>
          </div>
        </div>

        {/* Whitepaper references */}
        {contract.sections.length > 0 && (
          <div className="contract-sections">
            <span className="contract-sections-label">Contract Reference:</span>
            {contract.sections.map((s, i) => (
              <span key={i} className="contract-section-tag">{s}</span>
            ))}
          </div>
        )}

        {/* View mode toggle + Review status */}
        <div className="contract-view-controls">
          <div className="contract-view-toggle">
          <button
            className={`toggle-btn ${viewMode === 'rules' ? 'active' : ''}`}
            onClick={() => onViewModeChange('rules')}
          >
            Rules &amp; Constants
          </button>
          <button
            className={`toggle-btn ${viewMode === 'decisions' ? 'active' : ''}`}
            onClick={() => onViewModeChange('decisions')}
          >
            Decision Tables
          </button>
          </div>

          {/* Review status dropdown */}
          <div className="contract-review-status">
            {reviewStatus !== null ? (
              <>
                <a 
                  href="/admin/contracts/reviews" 
                  className="back-to-reviews-link"
                  title="Back to Smart Contract Version Reviews"
                >
                  ← Reviews
                </a>
                <span className="review-status-divider">|</span>
                <span className="review-status-label">
                  {versionTag && <code className="review-version-tag">{versionTag}</code>}
                  Status:
                </span>
                <div className="status-dropdown-wrapper">
                  <button
                    ref={triggerRef}
                    className={`status-dropdown-trigger status-${reviewStatus.replace(/_/g, '-')}`}
                    onClick={() => {
                      if (!statusDropdownOpen && triggerRef.current) {
                        const rect = triggerRef.current.getBoundingClientRect();
                        setDropdownPos({ top: rect.bottom + 4, left: rect.left });
                      }
                      setStatusDropdownOpen(!statusDropdownOpen);
                    }}
                    disabled={statusLoading}
                    style={{ borderColor: currentStatusOption?.color }}
                  >
                    {statusLoading ? 'Saving...' : currentStatusOption?.label || reviewStatus}
                    <span className="dropdown-arrow">▾</span>
                  </button>
                  {statusDropdownOpen && dropdownPos && (
                    <div 
                      className="status-dropdown-menu-fixed"
                      style={{
                        position: 'fixed',
                        top: dropdownPos.top,
                        left: dropdownPos.left,
                        zIndex: 9999,
                        minWidth: 200,
                        width: 'max-content',
                        padding: '4px 0',
                        background: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: 8,
                        boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)',
                      }}
                    >
                      {STATUS_OPTIONS.map(opt => (
                        <button
                          key={opt.value}
                          className={`status-dropdown-item ${reviewStatus === opt.value ? 'active' : ''}`}
                          onClick={() => handleStatusChange(opt.value)}
                          style={{ '--status-color': opt.color } as React.CSSProperties}
                        >
                          <span className="status-dot" style={{ backgroundColor: opt.color }} />
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <span className="review-status-label review-status-none">
                Not in any pending review
              </span>
            )}
          </div>
        </div>
      </header>

      {/* Body */}
      <div className="contract-body">
        {viewMode === 'rules' ? (
          <RulesView contract={contract} />
        ) : (
          <DecisionTablesView contract={contract} />
        )}
      </div>

      {/* Services that use this contract */}
      {contract.services.length > 0 && (
        <div className="contract-services">
          <h3 className="contract-section-heading">Consuming Services</h3>
          <div className="contract-service-list">
            {contract.services.map((s, i) => (
              <span key={i} className="contract-service-tag">{s}</span>
            ))}
          </div>
        </div>
      )}

      {/* Source reference (collapsible) */}
      <div className="contract-source-section">
        <button
          className="contract-source-toggle"
          onClick={() => setShowSource(!showSource)}
        >
          <span className="contract-source-icon">{showSource ? '▾' : '▸'}</span>
          <span className="contract-source-path">{contract.sourceFile}</span>
        </button>
        {showSource && (
          <div className="contract-source-info">
            <p className="contract-source-note">
              This contract is implemented in{' '}
              <button
                className="contract-source-link"
                onClick={() => onViewSource(contract.sourceFile)}
                title="View source (requires re-authentication)"
              >
                {contract.sourceFile}
              </button>
              {' '}within the <code>@cxxr/contracts</code> workspace package.
              The constants and validation functions shown above are exported
              from this module and imported by the consuming services listed above.
            </p>
            <div className="contract-source-constants-code">
              <div className="code-header">Exported Constants</div>
              <pre className="code-block">
                {contract.constants
                  .map(c => {
                    const val = typeof c.value === 'string' ? `'${c.value}'` : c.value;
                    const comment = c.section ? `  // ${c.section}` : '';
                    return `export const ${c.name} = ${val};${comment}`;
                  })
                  .join('\n')}
              </pre>
            </div>
            <button
              className="contract-view-source-btn"
              onClick={() => onViewSource(contract.sourceFile)}
            >
              🔒 View Full Source Code
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// RULES VIEW
// =============================================================================

function RulesView({ contract }: { contract: ContractDefinition }) {
  return (
    <div className="rules-view">
      {/* Constants table */}
      <div className="constants-section">
        <h3 className="contract-section-heading">Parameters</h3>
        <table className="constants-table">
          <thead>
            <tr>
              <th>Constant</th>
              <th>Value</th>
              <th>Unit</th>
              <th>Reference</th>
            </tr>
          </thead>
          <tbody>
            {contract.constants.map(c => (
              <tr key={c.name}>
                <td>
                  <code className="constant-name">{c.name}</code>
                </td>
                <td className="constant-value">
                  {typeof c.value === 'boolean'
                    ? c.value ? 'true' : 'false'
                    : typeof c.value === 'number'
                      ? c.value.toLocaleString()
                      : c.value}
                </td>
                <td className="constant-unit">{c.unit || '—'}</td>
                <td className="constant-section">{c.section || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Rules in English prose */}
      <div className="rules-section">
        <h3 className="contract-section-heading">Business Rules</h3>
        {contract.rules.map(rule => (
          <div key={rule.id} className="rule-card">
            <div className="rule-header">
              <code className="rule-id">{rule.id}</code>
              {rule.section && <span className="rule-section">{rule.section}</span>}
            </div>
            <p className="rule-summary">{rule.summary}</p>
            {rule.details && <p className="rule-details">{rule.details}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// DECISION TABLES VIEW
// =============================================================================

function DecisionTablesView({ contract }: { contract: ContractDefinition }) {
  const rulesWithDecisions = contract.rules.filter(r => r.decisions?.length);

  if (!rulesWithDecisions.length) {
    return (
      <div className="decisions-empty">
        No decision tables defined for this contract.
      </div>
    );
  }

  return (
    <div className="decisions-view">
      {rulesWithDecisions.map(rule => (
        <div key={rule.id} className="decision-table-section">
          <div className="decision-table-header">
            <code className="rule-id">{rule.id}</code>
            {rule.section && <span className="rule-section">{rule.section}</span>}
          </div>
          <p className="decision-table-summary">{rule.summary}</p>
          <table className="decision-table">
            <thead>
              <tr>
                <th className="dt-condition-header">Condition</th>
                <th className="dt-outcome-header">Outcome</th>
              </tr>
            </thead>
            <tbody>
              {rule.decisions!.map((d, i) => (
                <DecisionRowComponent key={i} decision={d} />
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

function DecisionRowComponent({ decision }: { decision: DecisionRow }) {
  const typeClass = `dt-type-${decision.type}`;
  const typeLabel: Record<string, string> = {
    allow: '✓',
    deny: '✗',
    fee: '$',
    info: 'ℹ',
  };

  return (
    <tr className={`decision-row ${typeClass}`}>
      <td className="dt-condition">{decision.condition}</td>
      <td className="dt-outcome">
        <span className={`dt-indicator ${typeClass}`}>{typeLabel[decision.type]}</span>
        {decision.outcome}
      </td>
    </tr>
  );
}

export default ContractsPage;
